import React, { useState, useCallback, useEffect } from 'react';

const PAYMENT_LABELS = {
  cash:    { label: 'Gotówka',  icon: '💵' },
  card:    { label: 'Karta',    icon: '💳' },
  blik:    { label: 'BLIK',     icon: '📱' },
  receipt: { label: 'Paragon',  icon: '🧾' },
  unknown: { label: 'Nieznana', icon: '❓' },
};

// Formatuje datę używając lokalnych składowych (bez konwersji do UTC),
// żeby uniknąć przesunięcia o dzień w strefach czasowych innych niż UTC.
function toLocalISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function today() {
  return toLocalISO(new Date());
}

function weekAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return toLocalISO(d);
}

export default function History() {
  const [from, setFrom]       = useState(today());
  const [to, setTo]           = useState(today());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const token = localStorage.getItem('cafepos_token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/orders/history?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(await res.json());
    } finally { setLoading(false); }
  }, [from, to, token]);

  // Wczytaj dzisiejsze rachunki automatycznie zaraz po wejściu w widok
  useEffect(() => { load(); }, []);

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function formatDate(str) {
    if (!str) return '—';
    return new Date(str).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 20 }}>Historia rachunków</h2>

      {/* Filtry */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 20, background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px 16px', flexWrap: 'wrap' }}>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 140 }}>
          <label>Od</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 140 }}>
          <label>Do</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn-sm" onClick={() => { setFrom(today()); setTo(today()); }}>Dziś</button>
          <button className="btn-sm" onClick={() => { setFrom(weekAgo()); setTo(today()); }}>7 dni</button>
        </div>
        <button className="btn-primary" style={{ width: 'auto', padding: '9px 20px' }} onClick={load} disabled={loading}>
          {loading ? 'Ładowanie...' : 'Szukaj'}
        </button>
      </div>

      {/* Podsumowanie */}
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Rachunków</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>{data.summary.count}</div>
          </div>
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Łączna kwota</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>{Number(data.summary.total).toFixed(2)} zł</div>
          </div>
          {Object.entries(data.summary.by_method || {}).map(([method, total]) => {
            const m = PAYMENT_LABELS[method] || PAYMENT_LABELS.unknown;
            return (
              <div key={method} style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{m.icon} {m.label}</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{Number(total).toFixed(2)} zł</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lista rachunków */}
      {data && data.orders.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
          Brak rachunków w wybranym okresie
        </div>
      )}

      {data && data.orders.map(order => {
        const isOpen = expanded.has(order.id);
        const pm = PAYMENT_LABELS[order.payment_method] || PAYMENT_LABELS.unknown;
        const tableName = order.table_name || `Stolik ${order.table_number}`;
        return (
          <div key={order.id} style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}
              onClick={() => toggleExpand(order.id)}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>#{order.id} {tableName}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {formatDate(order.paid_at)}
                  {order.operator_name && <span> · {order.operator_name}</span>}
                </div>
              </div>
              <span style={{ fontSize: 13, background: 'var(--bg)', padding: '3px 8px', borderRadius: 4 }}>
                {pm.icon} {pm.label}
              </span>
              <span style={{ fontSize: 15, fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
                {Number(order.total).toFixed(2)} zł
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isOpen ? '▲' : '▼'}</span>
            </div>

            {isOpen && (
              <div style={{ borderTop: '0.5px solid var(--border)', padding: '10px 16px' }}>
                {order.items.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: i < order.items.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
                    <span style={{ minWidth: 24, fontWeight: 600, color: 'var(--text-muted)' }}>{item.qty}×</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13 }}>{item.name}</div>
                      {item.variant_label && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.variant_label}</div>}
                      {item.modifier_names && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>+ {item.modifier_names}</div>}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{(item.price * item.qty).toFixed(2)} zł</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, fontWeight: 600, fontSize: 14 }}>
                  <span>Razem</span>
                  <span>{Number(order.total).toFixed(2)} zł</span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
